import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  adminFetchMe,
  adminSignIn,
  clearAdminSession,
  createAdminPresentation,
  getPublicAppUrl,
  getStoredAccessToken,
  publicPresentationPdfUrl,
  publicPresentationUrl,
} from "@/lib/adminApi";

type OwnerKnows = "yes" | "no" | null;
type Phase = "form" | "loading" | "ready" | "error";

function isStreetEasyUrl(value: string): boolean {
  try {
    const u = new URL(value.trim());
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname.toLowerCase();
    return host === "streeteasy.com" || host === "www.streeteasy.com";
  } catch {
    return false;
  }
}

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : "";
  if (/streeteasy|source|listing/i.test(msg)) {
    return "StreetEasy data could not be retrieved for this listing.";
  }
  if (/invalid|url/i.test(msg)) {
    return "Enter a valid StreetEasy listing URL.";
  }
  return "Couldn’t generate this presentation. Please try again.";
}

export default function Create() {
  const navigate = useNavigate();
  const [booting, setBooting] = React.useState(true);
  const [email, setEmail] = React.useState("Alain.Onesti@elliman.com");
  const [password, setPassword] = React.useState("");
  const [userEmail, setUserEmail] = React.useState<string | null>(null);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [signingIn, setSigningIn] = React.useState(false);

  const [streeteasyUrl, setStreeteasyUrl] = React.useState("");
  const [knowsOwner, setKnowsOwner] = React.useState<OwnerKnows>("no");
  const [ownerName, setOwnerName] = React.useState("");
  const [phase, setPhase] = React.useState<Phase>("form");
  const [loadingStep, setLoadingStep] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [resultUrl, setResultUrl] = React.useState<string | null>(null);
  const [resultPdfUrl, setResultPdfUrl] = React.useState<string | null>(null);
  const [copyLabel, setCopyLabel] = React.useState("Copy Link");
  const [shareBusy, setShareBusy] = React.useState(false);
  const generatingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      await getPublicAppUrl();
      if (!getStoredAccessToken()) {
        if (!cancelled) setBooting(false);
        return;
      }
      try {
        const me = await adminFetchMe();
        if (!cancelled) setUserEmail(me.email);
      } catch {
        clearAdminSession();
        if (!cancelled) setUserEmail(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (phase !== "loading") return;
    setLoadingStep(0);
    const t = window.setTimeout(() => setLoadingStep(1), 4000);
    return () => window.clearTimeout(t);
  }, [phase]);

  const onSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setAuthError(null);
    try {
      const me = await adminSignIn(email, password);
      setUserEmail(me.email);
      setPassword("");
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : "Sign in failed");
      setUserEmail(null);
    } finally {
      setSigningIn(false);
    }
  };

  const resetForm = () => {
    setStreeteasyUrl("");
    setKnowsOwner("no");
    setOwnerName("");
    setPhase("form");
    setError(null);
    setResultUrl(null);
    setResultPdfUrl(null);
    setCopyLabel("Copy Link");
    generatingRef.current = false;
  };

  const onGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (generatingRef.current) return;

    const url = streeteasyUrl.trim();
    if (!isStreetEasyUrl(url)) {
      setError("Enter a valid StreetEasy listing URL.");
      setPhase("error");
      return;
    }
    if (knowsOwner === "yes" && !ownerName.trim()) {
      setError("Enter the owner’s name, or choose No.");
      setPhase("error");
      return;
    }

    generatingRef.current = true;
    setError(null);
    setPhase("loading");
    setResultUrl(null);
    setResultPdfUrl(null);

    try {
      const result = await createAdminPresentation({
        streeteasy_url: url,
        owner_name: knowsOwner === "yes" ? ownerName.trim() : null,
      });
      if (result.status !== "ready" || !result.slug) {
        const code = result.error?.code || "";
        if (String(code).includes("STREETEASY") || String(code).includes("SOURCE")) {
          throw new Error("StreetEasy data could not be retrieved for this listing.");
        }
        throw new Error(result.error?.message || "Generation failed");
      }
      const canonical = publicPresentationUrl(result.slug);
      setResultUrl(result.url || canonical);
      setResultPdfUrl(result.pdf_url || publicPresentationPdfUrl(result.slug));
      setPhase("ready");
    } catch (err) {
      setError(friendlyError(err));
      setPhase("error");
    } finally {
      generatingRef.current = false;
    }
  };

  const onCopy = async () => {
    if (!resultUrl) return;
    try {
      await navigator.clipboard.writeText(resultUrl);
      setCopyLabel("Copied");
      window.setTimeout(() => setCopyLabel("Copy Link"), 2000);
    } catch {
      setCopyLabel("Copy failed");
      window.setTimeout(() => setCopyLabel("Copy Link"), 2000);
    }
  };

  const onSharePdf = async () => {
    const pdfHref = resultPdfUrl || (resultUrl ? `${resultUrl}/pdf` : null);
    if (!pdfHref || shareBusy) return;
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
      // Fallback: open download — same as Download PDF.
      window.location.href = pdfHref;
      return;
    }
    setShareBusy(true);
    try {
      const res = await fetch(pdfHref);
      if (!res.ok) throw new Error("PDF fetch failed");
      const blob = await res.blob();
      const file = new File([blob], "pitchai-presentation.pdf", {
        type: "application/pdf",
      });
      const payload: ShareData = {
        title: "PitchAI presentation",
        text: "Expired listing analysis PDF",
        files: [file],
      };
      if (navigator.canShare && !navigator.canShare(payload)) {
        await navigator.share({
          title: "PitchAI presentation",
          url: pdfHref,
        });
      } else {
        await navigator.share(payload);
      }
    } catch (err) {
      // User cancel is fine; other errors fall back to download.
      if (err instanceof DOMException && err.name === "AbortError") return;
      window.location.href = pdfHref;
    } finally {
      setShareBusy(false);
    }
  };

  if (booting) {
    return (
      <div className="min-h-screen bg-[#f4f3ef] text-charcoal" data-testid="create-boot">
        <div className="mx-auto max-w-lg px-4 py-16 text-sm text-charcoal/60">Loading…</div>
      </div>
    );
  }

  if (!userEmail) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#f4f3ef] px-4 text-charcoal"
        data-testid="create-login"
      >
        <form
          onSubmit={onSignIn}
          className="w-full max-w-sm border border-charcoal/10 bg-white p-8 shadow-sm"
        >
          <div className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-charcoal/55">
            PitchAI
          </div>
          <h1 className="mt-2 font-serif text-3xl text-charcoal">New presentation</h1>
          <p className="mt-2 text-sm text-charcoal/60">Sign in to create a seller presentation.</p>

          <label className="mt-8 block text-xs uppercase tracking-[0.14em] text-charcoal/55">
            Email
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
            />
          </label>

          <label className="mt-4 block text-xs uppercase tracking-[0.14em] text-charcoal/55">
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
            />
          </label>

          {authError && <p className="mt-4 text-sm text-red-700">{authError}</p>}

          <button
            type="submit"
            disabled={signingIn}
            className="mt-6 w-full bg-charcoal px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] text-ivory disabled:opacity-60"
          >
            {signingIn ? "Signing in…" : "Sign in"}
          </button>

          <p className="mt-4 text-center text-xs text-charcoal/50">
            <Link to="/admin" className="underline-offset-2 hover:underline">
              Back to admin
            </Link>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f3ef] text-charcoal" data-testid="create-page">
      <header className="border-b border-charcoal/10 bg-white">
        <div className="mx-auto flex max-w-lg flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-charcoal/55">
              PitchAI
            </div>
            <h1 className="mt-1 font-serif text-2xl">New presentation</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Link
              to="/admin"
              className="border border-charcoal/15 px-3 py-1.5 text-xs uppercase tracking-[0.12em] hover:border-charcoal/40"
            >
              Admin
            </Link>
            <button
              type="button"
              onClick={() => {
                clearAdminSession();
                setUserEmail(null);
                navigate("/create");
              }}
              className="border border-charcoal/15 px-3 py-1.5 text-xs uppercase tracking-[0.12em] hover:border-charcoal/40"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-8">
        {phase === "loading" && (
          <div
            className="border border-charcoal/10 bg-white px-6 py-10 text-center shadow-sm"
            data-testid="create-loading"
          >
            <p className="font-serif text-2xl text-charcoal">
              {loadingStep === 0 ? "Analyzing listing…" : "Preparing relaunch strategy…"}
            </p>
            <p className="mt-3 text-sm text-charcoal/55">This usually takes under a minute.</p>
          </div>
        )}

        {phase === "ready" && resultUrl && (
          <div
            className="border border-charcoal/10 bg-white px-6 py-10 shadow-sm"
            data-testid="create-ready"
          >
            <p className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-charcoal/55">
              Done
            </p>
            <h2 className="mt-2 font-serif text-3xl">Presentation Ready</h2>
            <p className="mt-3 break-all text-sm text-charcoal/60">{resultUrl}</p>
            <div className="mt-8 flex flex-col gap-3">
              <a
                href={resultUrl}
                target="_blank"
                rel="noreferrer"
                className="bg-charcoal px-4 py-3 text-center text-xs font-medium uppercase tracking-[0.16em] text-ivory"
                data-testid="create-open"
              >
                Open Presentation
              </a>
              <a
                href={resultPdfUrl || `${resultUrl}/pdf`}
                className="border border-charcoal/15 px-4 py-3 text-center text-xs font-medium uppercase tracking-[0.16em] hover:border-charcoal/40"
                data-testid="create-download-pdf"
              >
                Download PDF
              </a>
              {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
                <button
                  type="button"
                  onClick={onSharePdf}
                  disabled={shareBusy}
                  className="border border-charcoal/15 px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] hover:border-charcoal/40 disabled:opacity-60"
                  data-testid="create-share-pdf"
                >
                  {shareBusy ? "Preparing…" : "Share PDF"}
                </button>
              )}
              <button
                type="button"
                onClick={onCopy}
                className="border border-charcoal/15 px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] hover:border-charcoal/40"
                data-testid="create-copy"
              >
                {copyLabel}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="border border-charcoal/15 px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] hover:border-charcoal/40"
                data-testid="create-another"
              >
                Create Another
              </button>
            </div>
          </div>
        )}

        {(phase === "form" || phase === "error") && (
          <form
            onSubmit={onGenerate}
            className="border border-charcoal/10 bg-white p-6 shadow-sm sm:p-8"
            data-testid="create-form"
          >
            <label className="block text-xs uppercase tracking-[0.14em] text-charcoal/55">
              StreetEasy Listing URL
              <input
                type="url"
                required
                value={streeteasyUrl}
                onChange={(e) => setStreeteasyUrl(e.target.value)}
                placeholder="https://streeteasy.com/..."
                className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
                data-testid="create-url"
              />
            </label>

            <fieldset className="mt-6">
              <legend className="text-xs uppercase tracking-[0.14em] text-charcoal/55">
                Do you know the owner’s name?
              </legend>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setKnowsOwner("yes")}
                  className={`flex-1 border px-3 py-2.5 text-xs uppercase tracking-[0.12em] ${
                    knowsOwner === "yes"
                      ? "border-charcoal bg-charcoal text-ivory"
                      : "border-charcoal/15 hover:border-charcoal/40"
                  }`}
                  data-testid="create-owner-yes"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setKnowsOwner("no");
                    setOwnerName("");
                  }}
                  className={`flex-1 border px-3 py-2.5 text-xs uppercase tracking-[0.12em] ${
                    knowsOwner === "no"
                      ? "border-charcoal bg-charcoal text-ivory"
                      : "border-charcoal/15 hover:border-charcoal/40"
                  }`}
                  data-testid="create-owner-no"
                >
                  No
                </button>
              </div>
            </fieldset>

            {knowsOwner === "yes" && (
              <label className="mt-6 block text-xs uppercase tracking-[0.14em] text-charcoal/55">
                Owner Name
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Miguel Santos"
                  className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
                  data-testid="create-owner-name"
                />
              </label>
            )}

            {error && (
              <p className="mt-6 text-sm text-red-700" data-testid="create-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="mt-8 w-full bg-charcoal px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] text-ivory disabled:opacity-60"
              data-testid="create-generate"
            >
              Generate Presentation
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
